import type { ThemeFont } from '@mado/schema';
import {
  customFontFamily,
  fontFamilyStack,
  libraryFont,
  libraryFontCssUrl,
  type LibraryFontId,
} from '@mado/schema/fonts';
import { resolveAssetUrl } from './media';

/**
 * Loads a demo's `theme.font` into the document and returns the CSS
 * `font-family` stack the player's root should use.
 *
 * Only the type `ThemeFont` is imported from `@mado/schema`'s root — `import
 * type` is erased entirely at build time, so this never pulls zod into the
 * player (CLAUDE.md: "zod stays out of the player"). The runtime helpers
 * (`fontFamilyStack`, `customFontFamily`, `libraryFont`) come from the
 * zod-free `@mado/schema/fonts` subpath, the same convention `media.ts`,
 * `overlays.ts` and `runtime.ts` already use for `/assets`, `/camera` and
 * `/color`.
 *
 * Absent `theme.font` costs nothing: the caller (`runtime.ts`) never calls
 * this at all in that case, so there is no extra request, no injected tag,
 * and the root keeps the plain CSS `font:` shorthand from `styles.ts` byte
 * for byte — every document published before fonts existed renders exactly
 * as it always has.
 *
 * `@font-face` (and a `<link rel=stylesheet>` loading one) only takes effect
 * for the document it's declared in — there is no shadow root here to worry
 * about (the player mounts straight into the host element's light DOM, see
 * player.ts), but there IS more than one *document* in play:
 *   - `/e/:slug` (the iframe embed): `document` is the iframe's own page, so
 *     `document.head` is scoped to that one demo already.
 *   - `data-inline` (the web component mounted straight into a customer's
 *     page): `document` is the HOST page. Two `<mado-demo>`s on that page —
 *     same font or different fonts — share one `document.head`, so injection
 *     must be idempotent per font and never collide between fonts.
 *
 * Idempotence is checked against the live DOM, not a module-level `Set`:
 * `mountInline` (loader.ts) appends a fresh `<script src=".../player.js">`
 * tag for every inline embed on the page, and each `<script>` tag re-executes
 * the bundle in its own module scope even when the browser serves the file
 * from cache — a `Set` declared at module scope would not survive that and
 * would re-inject on the second embed. A DOM query does.
 */

const FONT_ATTR = 'data-mado-font';

/** Compared attribute by attribute, never interpolated into a selector: the
 *  key carries a document string (the custom font hash). */
function alreadyTagged(tag: 'link' | 'style', key: string): boolean {
  for (const el of document.querySelectorAll(`${tag}[${FONT_ATTR}]`)) {
    if (el.getAttribute(FONT_ATTR) === key) return true;
  }
  return false;
}

/** A URL inside CSS `url("…")`: drop anything that could close the string. */
function cssUrl(url: string): string {
  return url.replace(/["\\\n\r]/g, '');
}

/**
 * `{ kind: 'library', id }`: the font files are self-hosted with the player.
 * `/p/fonts/<id>.css` is a stylesheet — not a single file — because a library
 * font ships several weights and unicode-range subsets as separate
 * `@font-face` rules; that whole set loads as one `<link>`, and the browser
 * only fetches the subset/weight files a step actually uses.
 */
function loadLibraryFont(id: LibraryFontId, assetBase: string | null | undefined): void {
  const key = `library:${id}`;
  if (alreadyTagged('link', key)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = resolveAssetUrl(libraryFontCssUrl(id), assetBase);
  link.setAttribute(FONT_ATTR, key);
  document.head.appendChild(link);
}

/**
 * `{ kind: 'custom', ... }`: the creator's own uploaded font. Only WOFF2 is
 * ever stored or published (see `demo.ts` `ThemeFontFace`), so this is the
 * only format `src` needs. Each face's `woff2` is a root-relative `/a/...`
 * asset URL, resolved exactly the way every other asset in the document is
 * (`resolveAssetUrl`, the same function `media.ts` uses for step images and
 * `audio.ts` for narration/music/sfx) — never a baked-in host.
 */
function loadCustomFont(
  font: Extract<ThemeFont, { kind: 'custom' }>,
  assetBase: string | null | undefined,
): void {
  const key = `custom:${font.hash}`;
  if (alreadyTagged('style', key)) return;
  const family = customFontFamily(font.hash);
  const rules = font.faces
    .map(
      (f) =>
        `@font-face{font-family:"${family}";font-weight:${f.weight};` +
        `font-style:${f.style};font-display:swap;` +
        `src:url("${cssUrl(resolveAssetUrl(f.woff2, assetBase))}") format("woff2");}`,
    )
    .join('');
  const style = document.createElement('style');
  style.setAttribute(FONT_ATTR, key);
  style.textContent = rules;
  document.head.appendChild(style);
}

/**
 * Ensures the document has whatever `font` needs, and returns the CSS
 * `font-family` value the player's root should be set to.
 *
 * An unknown/malformed library id (a hand-edited document, or a future id
 * this build predates) makes no request at all — `libraryFont` returns
 * `undefined`, `fontFamilyStack` already falls back to the system stack for
 * that case, and nothing is injected into the DOM.
 */
export function applyThemeFont(
  font: ThemeFont | null | undefined,
  assetBase: string | null | undefined,
): string {
  if (font && typeof document !== 'undefined') {
    if (font.kind === 'library') {
      const lib = libraryFont(font.id);
      if (lib) loadLibraryFont(lib.id as LibraryFontId, assetBase);
    } else {
      loadCustomFont(font, assetBase);
    }
  }
  return fontFamilyStack(font);
}
